import React, { useRef, useState } from 'react';
import { Helmet } from 'react-helmet';
import { useTranslation } from 'react-i18next';
import { FormProvider } from 'react-hook-form';
import {
  Tabs,
  Tab,
  Box,
  Grid2 as Grid,
  TextField,
  Button,
  Card,
  CardContent,
  Typography,
  Divider,
  Chip,
  InputLabel
} from '@mui/material';
import { profileFormFields } from './helper';
import './UserProfile.scss';
import { UserProfileModel } from './UserProfileModel';
import { UserLanguages } from './Sections/UserLanguages';

import { Company } from 'types/Companies';
import { Link } from 'react-router-dom';
import { FormEntry } from './FormEntry';
import { UserPreferences } from 'components/pages/UserPreferences/UserPreferences';
import { PhoneField } from 'components/pages/register/Register/fields/PhoneField';
import useIsTextInDOM from 'hooks/useIsTextVisible';

export const SHIPPING_SECTION_ID = 'shipping';

const UserProfile = () => {
  const { companies, isSaving, languages, methods, onSubmit } =
    UserProfileModel();
  const isElementWithTextVisible = useIsTextInDOM('Shipping address');

  const { t } = useTranslation();
  const [tab, setTab] = useState<'profile' | 'preferences'>('profile');
  const scrollToRef = useRef<HTMLDivElement>(null);

  if (isElementWithTextVisible) {
    if (
      scrollToRef.current &&
      window.location.hash === `#${SHIPPING_SECTION_ID}`
    ) {
      scrollToRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }

  if (methods.getValues().segments === undefined) return null;

  const handleChange = (
    event: React.SyntheticEvent,
    newValue: 'profile' | 'preferences'
  ) => {
    setTab(newValue);
  };

  return (
    <FormProvider {...methods}>
      <Helmet>
        <title>Profile</title>
      </Helmet>
      <Box sx={{ maxWidth: 1400, margin: 'auto', p: 2 }}>
        <Tabs
          sx={theme => ({
            mt: 1,
            background: 'white',
            borderBottom: `1px solid ${theme.palette.divider}`
          })}
          value={tab}
          onChange={handleChange}
          aria-label="Profile Tabs"
        >
          <Tab label="Profile" value="profile" />
          <Tab label="Preferences" value="preferences" />
          <Divider />
        </Tabs>

        {tab === 'profile' && (
          <Box component="form" onSubmit={methods.handleSubmit(onSubmit)}>
            <Card>
              <Card>
                <CardContent>
                  <ProfileTitleSection title={'About'} />
                  <Grid container spacing={2} sx={{ mt: 2 }}>
                    <Grid size={{ xs: 12, md: 6 }}>
                      <FormEntry
                        field={profileFormFields.salutation}
                        style={{ minWidth: '150px' }}
                      />
                    </Grid>
                  </Grid>
                  <Grid container spacing={2} sx={{ mt: 2 }}>
                    <Grid size={{ xs: 12, md: 6 }}>
                      <FormEntry
                        className="required"
                        required={true}
                        field={profileFormFields.firstname}
                      />
                    </Grid>
                    <Grid size={{ xs: 12, md: 6 }}>
                      <FormEntry
                        className="required"
                        required={true}
                        field={profileFormFields.lastname}
                      />
                    </Grid>
                    <Grid size={{ xs: 12, md: 6 }}>
                      <FormEntry
                        required={true}
                        field={profileFormFields.country_code}
                        className="required"
                      />
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>

              <Card sx={{ mt: 3 }}>
                <CardContent>
                  <ProfileTitleSection title={'Contacts'} />
                  <Grid container spacing={2} sx={{ mt: 2 }}>
                    <Grid size={{ xs: 12, md: 6 }}>
                      <Box sx={{ mt: 1 }}>
                        <FormEntry field={profileFormFields.email} />
                      </Box>
                    </Grid>
                    <Grid size={{ xs: 12, md: 6 }}>
                      <PhoneField name="phone_number" />
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>

              <Card sx={{ mt: 3 }}>
                <CardContent>
                  <ProfileTitleSection title={'Professional bio'} />
                  <FormEntry
                    style={{ width: '100%' }}
                    field={profileFormFields.bio}
                  />
                </CardContent>
              </Card>

              <Card sx={{ mt: 3 }}>
                <CardContent>
                  <ProfileTitleSection title={'Current Position'} />
                  <Grid container spacing={2} sx={{ mt: 2 }}>
                    <Grid size={{ xs: 12 }}>
                      <FormEntry
                        className="required"
                        required={true}
                        field={profileFormFields.job_title}
                      />
                    </Grid>
                    <Grid size={{ xs: 12 }}>
                      <FormEntry
                        required={true}
                        field={profileFormFields.department}
                        className="required"
                      />
                    </Grid>
                  </Grid>
                  <Box sx={{ mt: 2 }}>
                    <FormEntry
                      required={true}
                      className="required"
                      field={profileFormFields.segments}
                    />
                  </Box>

                  <Box sx={{ mt: 2 }}>
                    <InputLabel>Company</InputLabel>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                      {companies &&
                        companies.map((company: Company, index: number) => (
                          <Link
                            key={index}
                            to={`/admin/companies/editor/${company.key}`}
                            style={{ textDecoration: 'none' }}
                          >
                            <Chip label={company.name} variant="outlined" />
                          </Link>
                        ))}
                    </Box>
                  </Box>
                </CardContent>
              </Card>

              <Card sx={{ mt: 3 }}>
                <CardContent>
                  <ProfileTitleSection title={'Social Media'} />
                  <TextField
                    fullWidth
                    label="LinkedIn"
                    placeholder={t(`user.socialMedia.linkedin`)}
                    {...methods.register(`media[0].url` as any)}
                    variant="outlined"
                    sx={{ mt: 2 }}
                  />
                </CardContent>
              </Card>

              <Card sx={{ mt: 3 }}>
                <CardContent>
                  <ProfileTitleSection title={'Languages'} />
                  <UserLanguages languagesOptions={languages} />
                </CardContent>
              </Card>

              <Card sx={{ mt: 3 }}>
                <CardContent>
                  <ProfileTitleSection title={'Interests'} />
                  <FormEntry field={profileFormFields.productInterests} />
                </CardContent>
              </Card>

              <Card sx={{ mt: 3 }} ref={scrollToRef}>
                <CardContent>
                  <ProfileTitleSection title={'Shipping address'} />
                  <Typography variant="body2" sx={{ mb: 2 }}>
                    Setting a shipping address will help us prefill forms when
                    you make a request.
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid size={{ xs: 12, md: 6 }}>
                      <FormEntry
                        field={profileFormFields.shipping_address.company}
                      />
                    </Grid>
                  </Grid>
                  <Grid container spacing={2}>
                    <Grid size={{ xs: 12, md: 6 }}>
                      <FormEntry
                        field={
                          profileFormFields.shipping_address.personOfContact
                        }
                      />
                    </Grid>
                    <Grid size={{ xs: 12, md: 12 }}>
                      <FormEntry
                        placeholder="Name and number of the street"
                        field={profileFormFields.shipping_address.street}
                      />
                    </Grid>
                    <Grid size={{ xs: 12, md: 12 }}>
                      <FormEntry
                        placeholder="Apartment, building, floor, etc."
                        field={
                          profileFormFields.shipping_address.street_additional
                        }
                      />
                    </Grid>
                    <Grid size={{ xs: 12, md: 3 }}>
                      <FormEntry
                        field={profileFormFields.shipping_address.postal_code}
                      />
                    </Grid>
                    <Grid size={{ xs: 12, md: 3 }}>
                      <FormEntry
                        field={profileFormFields.shipping_address.city}
                      />
                    </Grid>
                    <Grid size={{ xs: 12, md: 6 }}>
                      <FormEntry
                        field={profileFormFields.shipping_address.country}
                      />
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            </Card>
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3 }}>
              <Button
                type="submit"
                variant="contained"
                color="primary"
                disabled={!methods.formState.isDirty || isSaving}
              >
                Save
              </Button>
            </Box>
          </Box>
        )}
        {tab === 'preferences' && (
          <UserPreferences tab={tab} methods={methods} />
        )}
      </Box>
    </FormProvider>
  );
};

export default UserProfile;

export const ProfileTitleSection = ({
  title,
  optionalTitle
}: {
  title: string;
  optionalTitle?: string;
}) => {
  return (
    <>
      <Box display={'flex'} justifyContent={'space-between'}>
        <Typography variant="body2" mb={1}>
          {title}
        </Typography>
        <Typography variant="body2" mb={1}>
          {optionalTitle}
        </Typography>
      </Box>
      <Divider sx={{ marginX: '-20px', mb: 3 }} />
    </>
  );
};
